/**
 * Blood Pressure Validation Utilities
 * 
 * Validates BP inputs and parses text format (e.g., "120/80") to structured data.
 * Distinguishes between valid, invalid, and missing inputs for clinical review.
 */

export interface BloodPressureValue {
  systolic?: number;
  diastolic?: number;
  isValid: boolean;
  isInvalid: boolean;  // Specifically marks user provided invalid input (vs no input)
  rawInput?: string;   // Stores original text input for clinician review
  validationError?: string;
}

/**
 * Parse and validate blood pressure input from various formats
 * @param input - Raw input from patient (string, number, or object)
 * @returns Structured BP data with validation status
 */
export function parseBloodPressure(input: any): BloodPressureValue {
  // Handle null/undefined (no input provided)
  if (input === null || input === undefined || input === '') {
    return {
      isValid: false,
      isInvalid: false, // No input provided
    };
  }

  // Handle already structured object
  if (typeof input === 'object' && !Array.isArray(input)) {
    // Check if it has the invalid flag already set
    if ('isInvalid' in input && input.isInvalid) {
      return {
        systolic: input.systolic,
        diastolic: input.diastolic,
        isValid: false,
        isInvalid: true,
        rawInput: input.rawInput || String(input),
        validationError: input.validationError || 'Invalid blood pressure reading'
      };
    }

    const systolic = normalizeBPValue(input.systolic);
    const diastolic = normalizeBPValue(input.diastolic);
    const rawInput = input.rawInput || `${input.systolic}/${input.diastolic}`;
    
    // Validate numeric values if present
    if (systolic !== undefined || diastolic !== undefined) {
      const systolicValid = systolic === undefined || (typeof systolic === 'number' && isValidBPNumber(systolic, 'systolic'));
      const diastolicValid = diastolic === undefined || (typeof diastolic === 'number' && isValidBPNumber(diastolic, 'diastolic'));
      
      if (systolicValid && diastolicValid) {
        return {
          systolic,
          diastolic,
          isValid: true,
          isInvalid: false,
        };
      } else {
        return {
          systolic,
          diastolic,
          isValid: false,
          isInvalid: true,
          rawInput,
          validationError: 'Blood pressure values out of valid range'
        };
      }
    }
  }

  // Handle string input (e.g., "120/80")
  if (typeof input === 'string') {
    const trimmed = input.trim();
    
    // Check for slash format
    if (trimmed.includes('/')) {
      const parts = trimmed.split('/');
      
      if (parts.length === 2) {
        const systolic = parseFloat(parts[0].trim());
        const diastolic = parseFloat(parts[1].trim());
        
        // Check if parsing was successful
        if (!isNaN(systolic) && !isNaN(diastolic)) {
          // Validate ranges
          if (isValidBPNumber(systolic, 'systolic') && isValidBPNumber(diastolic, 'diastolic')) {
            return {
              systolic,
              diastolic,
              isValid: true,
              isInvalid: false,
              rawInput: trimmed,
            };
          } else {
            return {
              systolic,
              diastolic,
              isValid: false,
              isInvalid: true,
              rawInput: trimmed,
              validationError: 'Blood pressure values out of valid range (systolic: 40-300, diastolic: 20-200)'
            };
          }
        }
      }
    }
    
    // Try to parse as a single number (treat as systolic only)
    const singleValue = parseFloat(trimmed);
    if (!isNaN(singleValue) && isValidBPNumber(singleValue, 'systolic')) {
      return {
        systolic: singleValue,
        isValid: true,
        isInvalid: false,
        rawInput: trimmed,
      };
    }
    
    // Invalid format
    return {
      isValid: false,
      isInvalid: true,
      rawInput: trimmed,
      validationError: 'Invalid blood pressure format. Expected format: systolic/diastolic (e.g., 120/80)'
    };
  }

  // Handle single number input
  if (typeof input === 'number') {
    if (isValidBPNumber(input, 'systolic')) {
      return {
        systolic: input,
        isValid: true,
        isInvalid: false,
        rawInput: String(input),
      };
    } else {
      return {
        systolic: input,
        isValid: false,
        isInvalid: true,
        rawInput: String(input),
        validationError: 'Blood pressure value out of valid range'
      };
    }
  }

  // Unknown format
  return {
    isValid: false,
    isInvalid: true,
    rawInput: String(input),
    validationError: 'Unrecognized blood pressure input format'
  };
}

// Normalize mixed BP inputs so numeric strings are treated as numbers; leave other
// unexpected types untouched so they fail validation downstream.
function normalizeBPValue(value: any): any {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'string') {
    const parsed = parseFloat(value.trim());
    return Number.isFinite(parsed) ? parsed : value;
  }
  return value;
}

/**
 * Validate if a number is within acceptable BP range
 * @param value - Numeric value to validate
 * @param type - Whether this is systolic or diastolic
 * @returns true if valid, false otherwise
 */
function isValidBPNumber(value: number, type: 'systolic' | 'diastolic'): boolean {
  if (!Number.isFinite(value)) {
    return false;
  }

  // Reasonable ranges for blood pressure
  // Systolic: typically 40-300 mmHg (covers extreme low and high)
  // Diastolic: typically 20-200 mmHg
  if (type === 'systolic') {
    return value >= 40 && value <= 300;
  } else {
    return value >= 20 && value <= 200;
  }
}

/**
 * Format blood pressure for display
 * @param bp - Blood pressure value object
 * @returns Formatted string for display
 */
export function formatBloodPressure(bp: BloodPressureValue): string {
  if (!bp.isValid && bp.isInvalid) {
    return bp.rawInput || 'Reading Outside Target Range';
  }
  
  if (bp.systolic !== undefined && bp.diastolic !== undefined) {
    return `${bp.systolic}/${bp.diastolic}`;
  } else if (bp.systolic !== undefined) {
    return `${bp.systolic}`;
  } else if (bp.diastolic !== undefined) {
    return `${bp.diastolic}`;
  }
  
  return 'No data';
}

/**
 * Check if a value looks like it might be blood pressure data
 * @param value - Value to check
 * @returns true if it looks like BP data
 */
export function isBloodPressureLike(value: any): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  
  // Check for object with systolic or diastolic
  if (typeof value === 'object' && !Array.isArray(value)) {
    return 'systolic' in value || 'diastolic' in value || 'isInvalid' in value;
  }
  
  // Check for string with slash
  if (typeof value === 'string') {
    return value.includes('/');
  }
  
  return false;
}


// ============================================================================
// Blood Pressure Display & Classification (for PatientList)
// ============================================================================

/**
 * Blood pressure category based on AHA guidelines
 */
export type BPCategory = 'normal' | 'elevated' | 'high' | 'unknown';

/**
 * Simplified BP reading for display
 */
export interface BPReading {
  systolic?: number;
  diastolic?: number;
  date?: string;
}


export interface PatientBPData {
  answers?: any[];
  answers_by_prompt?: Record<string, Record<string, any>>;
  most_recent_surveys?: Array<{ answers?: any[]; created_at?: string }>;
}

/**
 * Classify blood pressure based on AHA guidelines
 * @param systolic - Systolic value
 * @param diastolic - Diastolic value
 * @returns Category: normal, elevated, high, or unknown
 */
export function classifyBloodPressure(systolic?: number, diastolic?: number): BPCategory {
  if (systolic === undefined && diastolic === undefined) return 'unknown';
  if (systolic !== undefined && diastolic !== undefined) {
    if (systolic < 140 && diastolic < 90) return 'normal';
    if (systolic >= 160 || diastolic >= 100) return 'high';
    if (systolic >= 140 || diastolic >= 90) return 'elevated';
    return 'unknown';
  }
  if (systolic !== undefined) {
    if (systolic < 140) return 'normal';
    if (systolic >= 160) return 'high';
    return 'elevated';
  }
  // diastolic only
  if (diastolic! < 90) return 'normal';
  if (diastolic! >= 100) return 'high';
  return 'elevated';
}

/**
 * Get badge color for BP category
 * @param category - BP category
 * @returns Color for UI badge
 */
export function getBPColor(category: BPCategory): 'green' | 'yellow' | 'red' | 'gray' {
  const colors: Record<BPCategory, 'green' | 'yellow' | 'red' | 'gray'> = {
    normal: 'green',
    elevated: 'yellow',
    high: 'red',
    unknown: 'gray'
  };
  return colors[category];
}

/**
 * Extract latest blood pressure reading from patient answers array
 * Structure: [{ "question": "...", "2026-01-19": { systolic, diastolic } }]
 * @param answers - Patient answers array
 * @returns Latest BP reading or null
 */
export function getLatestBloodPressure(patient?: PatientBPData): BPReading | null {
  if (!patient) return null;

  const readings: BPReading[] = [];

  // Source 1: Direct answers array
  if (patient.answers && Array.isArray(patient.answers)) {
    for (const answer of patient.answers) {
      if (!answer.question?.toLowerCase().includes('blood pressure')) continue;
      const dateKeys = Object.keys(answer).filter(k => k !== 'question').sort().reverse();
      for (const date of dateKeys) {
        const parsed = parseBloodPressure(answer[date]);
        if (parsed.isValid && (parsed.systolic !== undefined || parsed.diastolic !== undefined)) {
          readings.push({ systolic: parsed.systolic, diastolic: parsed.diastolic, date });
        }
      }
    }
  }

  // Source 2: answers_by_prompt
  if (patient.answers_by_prompt) {
    for (const [question, dateAnswers] of Object.entries(patient.answers_by_prompt)) {
      if (!question.toLowerCase().includes('blood pressure')) continue;
      for (const [date, value] of Object.entries(dateAnswers as Record<string, any>)) {
        const parsed = parseBloodPressure(value);
        if (parsed.isValid && (parsed.systolic !== undefined || parsed.diastolic !== undefined)) {
          readings.push({ systolic: parsed.systolic, diastolic: parsed.diastolic, date });
        }
      }
    }
  }

  // Source 3: most_recent_surveys
  if (patient.most_recent_surveys) {
    for (const survey of patient.most_recent_surveys) {
      if (!survey.answers) continue;
      for (const answer of survey.answers) {
        // Handle both array format and object format
        const question = (answer as any).question || (answer as any).prompt;
        if (!question?.toLowerCase().includes('blood pressure')) continue;
        const parsed = parseBloodPressure((answer as any).value || (answer as any).cast_answer || (answer as any).raw_answer);
        if (parsed.isValid && (parsed.systolic !== undefined || parsed.diastolic !== undefined)) {
          readings.push({
            systolic: parsed.systolic,
            diastolic: parsed.diastolic,
            date: (answer as any).created_at || survey.created_at
          });
        }
      }
    }
  }

  // Return the reading with the latest date
  if (readings.length === 0) return null;
  return readings.sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
}

/**
 * Get sort priority for BP (higher = more critical)
 * @param answers - Patient answers array
 * @returns Priority number for sorting (3=high, 2=elevated, 1=normal, 0=unknown, -1=no data)
 */
export function getBPSortPriority(patient?: PatientBPData): number {
  const bp = getLatestBloodPressure(patient);
  if (!bp?.systolic && !bp?.diastolic) return -1;
  if (bp?.systolic && bp?.diastolic) {
    // Mean Arterial Pressure = 1/3*(SBP) + 2/3*(DBP)
    return (bp.systolic / 3) + (2 * bp.diastolic / 3);
  }
  // Partial data: use whichever value is present
  return bp?.systolic ?? bp?.diastolic ?? -1;
}

/**
 * Compute urgency score for a question's answer set in the Answers tab.
 * Higher score = sorts higher. Questions are grouped by topic category:
 *
 *   Vitals (BP, glucose, weight, etc.)   10500–15000+
 *   Hospitalization                       5000–6000
 *   Meds adherence                        3000
 *   Diet & Exercise                       1000–2000
 *   Other (fallback)                     -2000–0
 */
export function getAnswerSetUrgencyScore(
  question: string,
  dateAnswers: Record<string, any>,
  options: {
    hospitalizationQuestion: string;
    positiveHospitalizationAnswers: Set<string>;
    getAnswerValue: (data: any) => any;
  }
): number {
  const { hospitalizationQuestion, positiveHospitalizationAnswers, getAnswerValue } = options;
  const entries = Object.entries(dateAnswers).filter(([k]) => k !== 'question');

  // --- data-type helpers ---
  const nonNull = entries.filter(([, data]) => {
    const v = getAnswerValue(data);
    return v !== null && v !== undefined;
  });
  const isNumeric = nonNull.length > 0 && nonNull.every(([, data]) => {
    const v = getAnswerValue(data);
    if (typeof v === 'boolean' || (typeof v === 'object' && v !== null)) return false;
    return !isNaN(Number(v));
  });
  const isBoolean = nonNull.length > 0 && nonNull.every(([, data]) => typeof getAnswerValue(data) === 'boolean');

  // --- 1. Blood pressure structural detection → Vitals ---
  const isBP = entries.some(([, data]) => {
    const v = getAnswerValue(data);
    return v !== null && v !== undefined &&
      typeof v === 'object' && ('systolic' in v || 'diastolic' in v);
  });

  if (isBP) {
    const bpReadings = entries
      .map(([date, data]) => {
        const v = getAnswerValue(data);
        if (!v || typeof v !== 'object') return null;
        const parsed = parseBloodPressure(v);
        if (!parsed.isValid) return null;
        return { date, systolic: parsed.systolic, diastolic: parsed.diastolic };
      })
      .filter(Boolean)
      .sort((a, b) => b!.date.localeCompare(a!.date));

    if (bpReadings.length === 0) return 11000;

    const latest = bpReadings[0]!;
    const category = classifyBloodPressure(latest.systolic, latest.diastolic);
    const secondary = latest.systolic ?? 0;

    if (category === 'high')     return 15000 + secondary;
    if (category === 'elevated') return 14000 + secondary;
    return 12000 + secondary;
  }

  // --- 2. Hospitalization exact match ---
  if (question === hospitalizationQuestion) {
    const sortedEntries = [...entries].sort(([a], [b]) => b.localeCompare(a));
    const hasPositive = sortedEntries.some(([, data]) => {
      const v = getAnswerValue(data);
      // PHA-993: the same answer arrives as a string ("yes"), as a real boolean
      // (the options-less render, PHA-857), or as a legacy yes_* key — and any
      // casing/padding from a voice transcription. A string-only test ranked a
      // reported hospitalization as if the patient had said no.
      if (typeof v === 'boolean') return v;
      if (typeof v !== 'string') return false;
      return positiveHospitalizationAnswers.has(v.trim().toLowerCase());
    });
    return hasPositive ? 6000 : 5000;
  }

  // --- keyword-based classification (first match wins) ---
  const qLower = question.toLowerCase();

  // 3. Meds keywords (checked before vitals so "cholesterol-lowering medications" → Meds)
  if (['prescribed', 'medication'].some(kw => qLower.includes(kw))) {
    return 3000;
  }

  // 4. Diet keywords
  if (['diet', 'meal plan'].some(kw => qLower.includes(kw))) {
    return isNumeric ? 2000 : 1500;
  }

  // 4b. Exercise keywords
  if (['exercise', 'physical activity'].some(kw => qLower.includes(kw))) {
    return isNumeric ? 1250 : 1000;
  }

  // 5. Hospitalization keywords (broader match)
  if (['hospital', 'emergency room'].some(kw => qLower.includes(kw))) {
    const sortedEntries = [...entries].sort(([a], [b]) => b.localeCompare(a));
    const hasPositive = sortedEntries.some(([, data]) => {
      const v = getAnswerValue(data);
      // PHA-993: the same answer arrives as a string ("yes"), as a real boolean
      // (the options-less render, PHA-857), or as a legacy yes_* key — and any
      // casing/padding from a voice transcription. A string-only test ranked a
      // reported hospitalization as if the patient had said no.
      if (typeof v === 'boolean') return v;
      if (typeof v !== 'string') return false;
      return positiveHospitalizationAnswers.has(v.trim().toLowerCase());
    });
    return hasPositive ? 6000 : 5000;
  }

  // 6. Vitals keywords
  if (['glucose', 'blood sugar', 'weight', 'heart rate', 'pulse', 'cholesterol', 'ldl', 'a1c', 'temperature', 'oxygen', 'peak flow', 'fev1', 'pef'].some(kw => qLower.includes(kw))) {
    return isNumeric ? 13000 : 10500;
  }

  // --- 7. Fallback (Other) ---
  if (isNumeric) return 0;
  if (isBoolean) return -1000;
  return -2000;
}
